import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import {
    DirectLineActivity,
    DirectLineActivityType,
    DirectLineConversationResponse,
    DirectLineOutboundActivity,
    DirectLineRole,
    DirectLineSessionData,
    DirectLineTokenRefreshResponse,
    SuggestedAction
} from '../models/directline.models';
import { CopilotService } from './copilot.service';

@Injectable({ providedIn: 'root' })
export class DirectLineService {

    readonly activities = signal<DirectLineActivity[]>([]);
    readonly connectionStatus = signal<'disconnected' | 'connecting' | 'connected' | 'error'>('disconnected');
    readonly suggestedActions = signal<SuggestedAction[]>([]);
    readonly sessionEnded = signal(false);
    readonly isWaitingForResponse = signal(false);

    private readonly DIRECTLINE_BASE = 'https://directline.botframework.com/v3/directline';
    private readonly MAX_RECONNECT_ATTEMPTS = 5;
    private readonly BASE_RECONNECT_DELAY_MS = 2000;
    private readonly TOKEN_REFRESH_BUFFER_S = 300;
    private readonly SESSION_KEY = 'scout-ai-session';
    private readonly MAX_ACTIVITIES = 500;
    private readonly MAX_POST_RETRIES = 3;
    private readonly SESSION_STORE_DEBOUNCE_MS = 5000;

    private conversationId: string = '';
    private token: string = '';
    private streamUrl: string = '';
    private watermark: string = '';
    private userId: string = '';
    private expiresIn: number = 0;

    private socket: WebSocket | null = null;
    private reconnectAttempts: number = 0;
    private isReconnecting: boolean = false;
    private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
    private sessionStoreTimer: ReturnType<typeof setTimeout> | null = null;
    private seenActivityIds = new Set<string>();
    private sendQueue: { text: string }[] = [];
    private isSending: boolean = false;

    private visibilityHandler: (() => void) | null = null;
    private beforeUnloadHandler: (() => void) | null = null;

    constructor(
        private http: HttpClient,
        private copilotService: CopilotService
    ) {}

    async initialize(authService: MsalService): Promise<string> {
        const account = authService.instance.getAllAccounts()[0];
        this.userId = account?.localAccountId || account?.homeAccountId || 'user';

        const existingSession = this.getStoredSession();

        if (existingSession) {
            this.hydrateFromSession(existingSession);
            await this.startConversation(
                existingSession.token,
                existingSession.conversationId,
                existingSession.watermark
            );
            if (this.activities().length === 0) {
                this.sendEvent('startConversation');
            }
        } else {
            const tokenResponse = await firstValueFrom(this.copilotService.getToken());
            await this.startConversation(tokenResponse.token);
            this.sendEvent('startConversation');
        }

        this.storeSessionImmediate();
        return this.userId;
    }

    handleSuggestedAction(action: SuggestedAction): void {
        if (action.type === 'openUrl' && action.value) {
            try {
                const url = new URL(action.value);
                if (url.protocol === 'http:' || url.protocol === 'https:') {
                    window.open(action.value, '_blank', 'noopener,noreferrer');
                }
            } catch { /* invalid URL */ }
        } else {
            this.sendMessage(action.value || action.title);
        }
    }

    sendMessage(text: string): void {
        if (!this.conversationId || !this.token || this.sessionEnded()) return;
        this.suggestedActions.set([]);
        this.isWaitingForResponse.set(true);
        this.storeSessionImmediate();
        this.sendQueue.push({ text });
        this.processSendQueue();
    }

    disconnect(): void {
        this.cancelTokenRefresh();
        this.cancelSessionStoreDebounce();
        this.unregisterVisibilityHandler();
        this.unregisterBeforeUnloadHandler();

        if (this.socket) {
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.close();
            this.socket = null;
        }

        this.isReconnecting = false;
        this.connectionStatus.set('disconnected');
    }

    clearHistory(): void {
        this.activities.set([]);
        this.suggestedActions.set([]);
        this.watermark = '';
        this.conversationId = '';
        this.token = '';
        this.expiresIn = 0;
        this.seenActivityIds.clear();
        this.sessionEnded.set(false);
        this.isWaitingForResponse.set(false);
        this.clearStoredSession();
    }

    storeSessionImmediate(): void {
        if (!this.conversationId || !this.token) return;
        const data: DirectLineSessionData = {
            token: this.token,
            conversationId: this.conversationId,
            expiresIn: this.expiresIn,
            storedAt: Date.now(),
            watermark: this.watermark,
            activities: this.activities(),
            isWaitingForResponse: this.isWaitingForResponse()
        };
        sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
    }

    getStoredSession(): DirectLineSessionData | null {
        try {
            const stored = sessionStorage.getItem(this.SESSION_KEY);
            if (!stored) return null;
            const data: DirectLineSessionData = JSON.parse(stored);
            const elapsed = Date.now() - data.storedAt;
            if (elapsed >= data.expiresIn * 1000) {
                sessionStorage.removeItem(this.SESSION_KEY);
                return null;
            }
            return data;
        } catch {
            return null;
        }
    }

    hydrateFromSession(session: DirectLineSessionData): void {
        if (session.activities?.length > 0) {
            this.activities.set(session.activities);
            for (const activity of session.activities) {
                if (activity.id) {
                    this.seenActivityIds.add(activity.id);
                }
            }
        }
        this.isWaitingForResponse.set(!!session.isWaitingForResponse);
    }

    private async startConversation(token: string, existingConversationId?: string, existingWatermark?: string): Promise<void> {
        this.token = token;
        this.sessionEnded.set(false);
        this.connectionStatus.set('connecting');

        try {
            if (existingConversationId) {
                this.conversationId = existingConversationId;
                this.watermark = existingWatermark || '';
                await this.reconnectConversation();
            } else {
                await this.openConversation();
            }

            this.connectWebSocket();
            this.scheduleTokenRefresh();
            this.registerVisibilityHandler();
            this.registerBeforeUnloadHandler();
        } catch (error: unknown) {
            this.connectionStatus.set('error');
            throw error;
        }
    }

    private sendEvent(name: string, value?: unknown): void {
        if (!this.conversationId || !this.token) return;
        const activity: DirectLineOutboundActivity = {
            type: DirectLineActivityType.EVENT,
            from: { id: this.userId },
            name: name
        };
        if (value !== undefined) {
            activity.value = value;
        }
        this.postActivity(activity);
    }

    private clearStoredSession(): void {
        sessionStorage.removeItem(this.SESSION_KEY);
    }

    private scheduleSessionStore(): void {
        this.cancelSessionStoreDebounce();
        this.sessionStoreTimer = setTimeout(() => this.storeSessionImmediate(), this.SESSION_STORE_DEBOUNCE_MS);
    }

    private cancelSessionStoreDebounce(): void {
        if (this.sessionStoreTimer) {
            clearTimeout(this.sessionStoreTimer);
            this.sessionStoreTimer = null;
        }
    }

    private async openConversation(): Promise<void> {
        const headers = this.authHeaders();
        const response = await firstValueFrom(
            this.http.post<DirectLineConversationResponse>(
                `${this.DIRECTLINE_BASE}/conversations`,
                {},
                { headers }
            )
        );
        this.conversationId = response.conversationId;
        this.token = response.token;
        this.expiresIn = response.expires_in;
        this.streamUrl = response.streamUrl;
    }

    private async reconnectConversation(): Promise<void> {
        const headers = this.authHeaders();
        const url = this.watermark
            ? `${this.DIRECTLINE_BASE}/conversations/${this.conversationId}?watermark=${this.watermark}`
            : `${this.DIRECTLINE_BASE}/conversations/${this.conversationId}`;

        const response = await firstValueFrom(
            this.http.get<DirectLineConversationResponse>(url, { headers })
        );
        this.token = response.token;
        this.expiresIn = response.expires_in;
        this.streamUrl = response.streamUrl;
    }

    private connectWebSocket(): void {
        if (!this.streamUrl) return;

        if (this.socket) {
            this.socket.onclose = null;
            this.socket.onerror = null;
            this.socket.close();
        }

        this.socket = new WebSocket(this.streamUrl);

        this.socket.onopen = () => {
            this.connectionStatus.set('connected');
            this.reconnectAttempts = 0;
        };

        this.socket.onmessage = (event: MessageEvent) => {
            this.handleWebSocketMessage(event.data);
        };

        this.socket.onclose = () => {
            if (this.connectionStatus() !== 'disconnected') {
                this.scheduleReconnect();
            }
        };

        this.socket.onerror = () => {
            // onerror is always followed by onclose — let onclose handle reconnect
        };
    }

    private handleWebSocketMessage(data: string): void {
        if (!data) return;

        try {
            const parsed = JSON.parse(data);
            if (parsed.watermark) {
                this.watermark = parsed.watermark;
                this.scheduleSessionStore();
            }

            if (!parsed.activities || !Array.isArray(parsed.activities)) return;

            const newActivities = this.processIncomingActivities(parsed.activities);

            if (newActivities.length > 0) {
                this.activities.update(acts => {
                    const combined = [...acts, ...newActivities];
                    return combined.length > this.MAX_ACTIVITIES
                        ? combined.slice(-this.MAX_ACTIVITIES)
                        : combined;
                });
                if (newActivities.some(a => a.from?.role === DirectLineRole.BOT && a.type === DirectLineActivityType.MESSAGE)) {
                    this.isWaitingForResponse.set(false);
                }
            }
        } catch (error: unknown) {
            console.warn('DirectLine: failed to parse WebSocket message', error);
        }
    }

    private processIncomingActivities(rawActivities: unknown[]): DirectLineActivity[] {
        const newActivities: DirectLineActivity[] = [];

        for (const activity of rawActivities) {
            if (!this.isValidActivity(activity)) continue;

            const a = activity as Record<string, unknown>;
            const id = a['id'] as string;
            const type = a['type'] as string;

            if (this.seenActivityIds.has(id)) continue;
            this.seenActivityIds.add(id);

            if (type === DirectLineActivityType.END_OF_CONVERSATION) {
                this.sessionEnded.set(true);
                continue;
            }

            if (type === DirectLineActivityType.MESSAGE) {
                const suggestedRaw = a['suggestedActions'] as { actions?: SuggestedAction[] } | undefined;
                if (suggestedRaw?.actions?.length) {
                    this.suggestedActions.set(suggestedRaw.actions);
                }
                newActivities.push(this.mapRawActivity(activity));
            }
        }

        return newActivities;
    }

    private mapRawActivity(raw: unknown): DirectLineActivity {
        const a = raw as Record<string, unknown>;
        return {
            id: a['id'] as string,
            type: a['type'] as string,
            timestamp: (a['timestamp'] as string) || new Date().toISOString(),
            from: (a['from'] as DirectLineActivity['from']) || { id: '', name: '', role: '' },
            text: (a['text'] as string) || '',
            attachments: ((a['attachments'] as DirectLineActivity['attachments']) || []).slice(0, 20),
            suggestedActions: (a['suggestedActions'] as DirectLineActivity['suggestedActions']) || null,
            value: a['value'] || null
        };
    }

    private isValidActivity(activity: unknown): boolean {
        const a = activity as Record<string, unknown>;
        return !!a
            && typeof a['id'] === 'string'
            && typeof a['type'] === 'string'
            && (Object.values(DirectLineActivityType) as string[]).includes(a['type'] as string)
            && !!a['from']
            && typeof (a['from'] as Record<string, unknown>)['id'] === 'string';
    }

    private async postActivity(activity: DirectLineOutboundActivity, retryCount: number = 0): Promise<void> {
        if (!this.conversationId || !this.token) return;

        const headers = this.authHeaders();
        try {
            await firstValueFrom(
                this.http.post(
                    `${this.DIRECTLINE_BASE}/conversations/${this.conversationId}/activities`,
                    activity,
                    { headers }
                )
            );
        } catch (error: unknown) {
            const status = error instanceof HttpErrorResponse ? error.status : 0;
            if (status === 429 && retryCount < this.MAX_POST_RETRIES) {
                await this.delay(1000 * Math.pow(2, retryCount));
                await this.postActivity(activity, retryCount + 1);
                return;
            }
            this.isWaitingForResponse.set(false);
            this.appendErrorActivity('Message failed to send. Please try again.');
            this.storeSessionImmediate();
        }
    }

    private appendErrorActivity(text: string): void {
        const errorActivity: DirectLineActivity = {
            id: `error-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            type: DirectLineActivityType.MESSAGE,
            timestamp: new Date().toISOString(),
            from: { id: 'system', name: 'System', role: DirectLineRole.BOT },
            text,
            attachments: [],
            suggestedActions: null,
            value: null
        };
        this.activities.update(acts => [...acts, errorActivity]);
    }

    private async processSendQueue(): Promise<void> {
        if (this.isSending || this.sendQueue.length === 0) return;

        this.isSending = true;
        while (this.sendQueue.length > 0) {
            const item = this.sendQueue.shift();
            if (!item) break;
            await this.postActivity({
                type: DirectLineActivityType.MESSAGE,
                from: { id: this.userId },
                text: item.text
            });
            if (this.sendQueue.length > 0) {
                await this.delay(500);
            }
        }
        this.isSending = false;
    }

    private scheduleReconnect(): void {
        if (this.isReconnecting) return;
        if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
            this.connectionStatus.set('error');
            return;
        }

        this.isReconnecting = true;
        this.connectionStatus.set('connecting');
        const delayMs = this.BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts);
        this.reconnectAttempts++;

        setTimeout(async () => {
            try {
                await this.reconnectConversation();
                this.connectWebSocket();
                this.storeSessionImmediate();
                this.isReconnecting = false;
            } catch (error: unknown) {
                console.warn('DirectLine: reconnect attempt failed', error);
                this.isReconnecting = false;
                this.scheduleReconnect();
            }
        }, delayMs);
    }

    private scheduleTokenRefresh(): void {
        this.cancelTokenRefresh();
        if (this.expiresIn <= 0) return;

        const refreshMs = (this.expiresIn - this.TOKEN_REFRESH_BUFFER_S) * 1000;
        if (refreshMs <= 0) return;

        this.tokenRefreshTimer = setTimeout(() => this.refreshToken(), refreshMs);
    }

    private async refreshToken(): Promise<void> {
        try {
            const headers = this.authHeaders();
            const response = await firstValueFrom(
                this.http.post<DirectLineTokenRefreshResponse>(
                    `${this.DIRECTLINE_BASE}/tokens/refresh`,
                    {},
                    { headers }
                )
            );
            this.token = response.token;
            this.expiresIn = response.expires_in;
            this.storeSessionImmediate();
            this.scheduleTokenRefresh();
        } catch (refreshError: unknown) {
            console.warn('DirectLine: token refresh failed, falling back to new token', refreshError);
            try {
                const tokenResponse = await firstValueFrom(this.copilotService.getToken());
                this.token = tokenResponse.token;
                this.expiresIn = tokenResponse.expiresIn;
                this.storeSessionImmediate();
                this.scheduleTokenRefresh();
            } catch (fallbackError: unknown) {
                console.warn('DirectLine: token fallback also failed', fallbackError);
                this.connectionStatus.set('error');
            }
        }
    }

    private cancelTokenRefresh(): void {
        if (this.tokenRefreshTimer) {
            clearTimeout(this.tokenRefreshTimer);
            this.tokenRefreshTimer = null;
        }
    }

    private registerVisibilityHandler(): void {
        this.unregisterVisibilityHandler();
        this.visibilityHandler = () => {
            if (document.visibilityState === 'visible' && this.conversationId) {
                if (this.socket?.readyState !== WebSocket.OPEN) {
                    this.reconnectAttempts = 0;
                    this.scheduleReconnect();
                }
                this.refreshTokenIfNeeded();
            }
        };
        document.addEventListener('visibilitychange', this.visibilityHandler);
    }

    private unregisterVisibilityHandler(): void {
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }
    }

    private registerBeforeUnloadHandler(): void {
        this.unregisterBeforeUnloadHandler();
        this.beforeUnloadHandler = () => this.storeSessionImmediate();
        window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }

    private unregisterBeforeUnloadHandler(): void {
        if (this.beforeUnloadHandler) {
            window.removeEventListener('beforeunload', this.beforeUnloadHandler);
            this.beforeUnloadHandler = null;
        }
    }

    private refreshTokenIfNeeded(): void {
        const session = this.getStoredSession();
        if (!session) return;
        const elapsed = Date.now() - session.storedAt;
        const refreshThreshold = (session.expiresIn - this.TOKEN_REFRESH_BUFFER_S) * 1000;
        if (elapsed >= refreshThreshold) {
            this.refreshToken();
        }
    }

    private authHeaders(): HttpHeaders {
        return new HttpHeaders({ 'Authorization': `Bearer ${this.token}` });
    }

    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}
